import {
  DEFAULT_API_REGION_SLUG,
  setApiRegionSlug,
} from "../helper/api-region-slug";
import {
  isPortalGoreloIoHost,
  resolveBaseDomain,
} from "../helper/public-function-access";
import { Enums } from "../helper/enums";
import { resolveBootEmail } from "../helper/resolve-boot-email";
import { authService } from "./auth-service";

/** Avoid duplicate contact KV fetches when bootstrap runs more than once per session. */
let lastContactLookupEmail: string | null = null;

/** KV lookup `region` / `Region`: `"2"` → au; anything else (including missing) → usw. */
function toApiGatewayRegionSlug(
  regionCode: string | number | null | undefined,
): Enums.ApiGatewayRegionSlug {
  if (String(regionCode).trim() === Enums.ApiGatewayRegionClaim.Australia) {
    return Enums.ApiGatewayRegionSlug.Australia;
  }

  return DEFAULT_API_REGION_SLUG;
}

function getRegionCodeFromLookupResponse(
  lookupResponse: unknown,
): string | number | null | undefined {
  if (!lookupResponse || typeof lookupResponse !== "object") {
    return undefined;
  }

  const response = lookupResponse as Record<string, unknown>;
  return (response.region ?? response.Region) as
    | string
    | number
    | null
    | undefined;
}

function getContactRegionLookupBaseUrl(): string {
  return (import.meta.env.VITE_regionLookupUrl || "").replace(/\/$/, "");
}

function getDomainRegionLookupBaseUrl(): string {
  return (import.meta.env.VITE_domainLookupUrl || "").replace(/\/$/, "");
}

export function buildRegionLookupUrlForEmail(email: string): string {
  const query = new URLSearchParams({
    email: email.trim(),
    isContact: "true",
  });
  return `${getContactRegionLookupBaseUrl()}?${query.toString()}`;
}

export function buildDomainLookupUrl(domain: string): string {
  const query = new URLSearchParams({
    domain: domain.trim(),
  });
  return `${getDomainRegionLookupBaseUrl()}?${query.toString()}`;
}

async function fetchRegionSlugFromLookupUrl(
  lookupUrl: string,
): Promise<Enums.ApiGatewayRegionSlug> {
  if (!lookupUrl.startsWith("http")) {
    return DEFAULT_API_REGION_SLUG;
  }

  try {
    const response = await fetch(lookupUrl);
    if (!response.ok) {
      return DEFAULT_API_REGION_SLUG;
    }
    const lookupResponse: unknown = await response.json();
    const regionCode = getRegionCodeFromLookupResponse(lookupResponse);
    return toApiGatewayRegionSlug(regionCode);
  } catch {
    return DEFAULT_API_REGION_SLUG;
  }
}

export async function fetchRegionSlugForEmail(
  email: string,
): Promise<Enums.ApiGatewayRegionSlug> {
  if (!email.trim()) {
    return DEFAULT_API_REGION_SLUG;
  }
  return fetchRegionSlugFromLookupUrl(buildRegionLookupUrlForEmail(email));
}

export async function fetchRegionSlugForDomain(
  domain: string,
): Promise<Enums.ApiGatewayRegionSlug> {
  if (!domain.trim()) {
    return DEFAULT_API_REGION_SLUG;
  }
  return fetchRegionSlugFromLookupUrl(buildDomainLookupUrl(domain));
}

function resolveContactLookupEmail(afterAuth = false): string | null {
  const cachedEmail = resolveBootEmail();
  if (cachedEmail) {
    return cachedEmail;
  }
  if (!afterAuth) {
    return null;
  }
  const authenticatedEmail = authService.GetAuthenticatedUserEmail()?.trim();
  return authenticatedEmail || null;
}

async function applyContactRegionFromEmail(email: string | null): Promise<void> {
  if (!email) {
    setApiRegionSlug(DEFAULT_API_REGION_SLUG);
    return;
  }
  if (email === lastContactLookupEmail) {
    return;
  }
  const regionSlug = await fetchRegionSlugForEmail(email);
  setApiRegionSlug(regionSlug);
  lastContactLookupEmail = email;
}

/**
 * portal.gorelo.io → contact-email KV (all URLs).
 * Custom domains → domain KV only (all URLs).
 */
export async function bootstrapRegionFromKv(options?: {
  afterAuth?: boolean;
}): Promise<void> {
  try {
    const baseDomain = resolveBaseDomain();
    const afterAuth = options?.afterAuth === true;

    if (isPortalGoreloIoHost(baseDomain)) {
      await applyContactRegionFromEmail(
        resolveContactLookupEmail(afterAuth),
      );
      return;
    }

    if (afterAuth) {
      return;
    }

    const regionSlug = baseDomain
      ? await fetchRegionSlugForDomain(baseDomain)
      : DEFAULT_API_REGION_SLUG;
    setApiRegionSlug(regionSlug);
  } catch {
    setApiRegionSlug(DEFAULT_API_REGION_SLUG);
  }
}
